import { createPatientClientUid } from '@/helpers/patient'
import { isNetworkError } from '@/helpers/networkStatus'
import i18n from '@/libs/i18n'
import { orderRequests, orderStatisticsRequests } from '../../http/requests/order/order'
import optimizeRoute from '../../http/requests/order/optimizeRoute'

// Add optimizeRoute to orderRequests
orderRequests.optimizeRoute = optimizeRoute

const ORDER_UPDATE_READ_ONLY_FIELDS = [
  'status',
  'team',
  'teamAssignments',
  'orderActivities',
  'version',
  'updatedAt',
  'createdAt',
  'blockedForRouting',
  'routeOptimizationMode',
  'isPrivate',
]

const ORDER_UPDATE_ROOT_REFERENCE_FIELDS = [
  'doctorUser',
  'customerAddress',
  'customerAddressDetails',
  'pickupAddress',
  'pickupAddressDetails',
  'deliveryAddress',
  'deliveryAddressDetails',
  'plannedTeamDefinition',
]

const ORDER_UPDATE_PATIENT_READ_ONLY_FIELDS = [
  'assignedTeam',
  'version',
  'position',
  'createdAt',
  'updatedAt',
]

const refreshOrderAfterUnconfirmedWorkflowAction = (dispatch, orderId) => {
  if (window?.toast?.notify?.info) {
    window.toast.notify.info(
      i18n.t('network.order-action-refresh.title'),
      i18n.t('network.order-action-refresh.description'),
    )
  }

  return dispatch('fetchOrderById', orderId)
    .then(() => {
      if (window?.toast?.notify?.success) {
        window.toast.notify.success(i18n.t('network.order-action-refresh.success'))
      }
    })
    .catch(() => {
      // The refresh request itself failed (e.g. still offline). Surface a distinct
      // message instead of leaking an unhandled rejection from fetchOrderById.
      if (window?.toast?.notify?.warning) {
        window.toast.notify.warning(
          i18n.t('network.order-action-refresh.title'),
          i18n.t('network.order-action-refresh.failure'),
        )
      }
    })
}

const isTeamActivelyAssignedToOrder = (order, teamId) => {
  if (!order || !teamId) {
    return false
  }

  const normalizedTeamId = Number(teamId)
  if (!Number.isFinite(normalizedTeamId)) {
    return false
  }

  if (Number(order?.team?.id) === normalizedTeamId) {
    return true
  }

  return Array.isArray(order.teamAssignments)
    && order.teamAssignments.some(assignment => (
      assignment
      && !assignment.releasedAt
      && Number(assignment?.team?.id) === normalizedTeamId
    ))
}

const extractReferenceId = value => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value ?? null
  }

  return value.id || null
}

const sanitizeOrderPatientInfo = patientInfo => {
  if (!Array.isArray(patientInfo)) {
    return patientInfo
  }

  return patientInfo.map(patient => {
    const sanitizedPatient = { ...patient }

    ORDER_UPDATE_PATIENT_READ_ONLY_FIELDS.forEach(field => {
      delete sanitizedPatient[field]
    })

    if (!sanitizedPatient.clientUid) {
      sanitizedPatient.clientUid = createPatientClientUid()
    }

    if (sanitizedPatient.id == null) {
      delete sanitizedPatient.id
    }

    sanitizedPatient.address = extractReferenceId(sanitizedPatient.address)
    sanitizedPatient.addressDetails = extractReferenceId(sanitizedPatient.addressDetails)

    return sanitizedPatient
  })
}

const sanitizeOrderComments = comments => {
  if (!Array.isArray(comments)) {
    return comments
  }

  return comments.map(comment => ({
    ...comment,
    relatedOrder: extractReferenceId(comment.relatedOrder),
  }))
}

const sanitizeOrderUpdatePayload = (payload, options = {}) => {
  const sanitizedPayload = { ...payload }

  ORDER_UPDATE_READ_ONLY_FIELDS.forEach(field => {
    delete sanitizedPayload[field]
  })

  if (options.allowStatusUpdate && 'status' in payload) {
    sanitizedPayload.status = payload.status
  }

  ORDER_UPDATE_ROOT_REFERENCE_FIELDS.forEach(field => {
    if (field in sanitizedPayload) {
      sanitizedPayload[field] = extractReferenceId(sanitizedPayload[field])
    }
  })

  if ('patientInfo' in sanitizedPayload) {
    sanitizedPayload.patientInfo = sanitizeOrderPatientInfo(sanitizedPayload.patientInfo)
  }

  if ('comments' in sanitizedPayload) {
    sanitizedPayload.comments = sanitizeOrderComments(sanitizedPayload.comments)
  }

  return sanitizedPayload
}

export default {
  fetchOrders: ({ commit }, query = {}) => new Promise((resolve, reject) => {
    orderRequests.getOrders(query)
      .then(response => {
        commit('SET_ORDERS', response.data.items)
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  fetchOrderById: ({ commit, state, dispatch }, id) => new Promise((resolve, reject) => {
    if (state.orderEdited.id !== null && parseInt(state.orderEdited.id, 10) !== parseInt(id, 10)) {
      commit('CLEAR_ORDER_EDITED')
    }
    orderRequests.getOrderById(id)
      .then(response => {
        commit('SET_ORDER_EDITED', response.data)
        dispatch('fetchOrderWorkflow', id)
          .then(() => resolve(response))
          .catch(error => reject(error))
      })
      .catch(error => {
        reject(error)
      })
  }),
  fetchOrderWorkflow: ({ commit }, id) => new Promise((resolve, reject) => {
    orderRequests.getOrderWorkflow(id)
      .then(response => {
        commit('SET_ORDER_WORKFLOW', response.data)
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  fetchOrderStatuses: ({ commit }) => new Promise((resolve, reject) => {
    orderRequests.getOrderStatuses()
      .then(response => {
        commit('SET_ORDER_STATUSES', response.data)
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  addOrder: (_, payload) => new Promise((resolve, reject) => {
    orderRequests.addOrder(payload)
      .then(response => {
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  cloneOrder: (_, id) => new Promise((resolve, reject) => {
    orderRequests.cloneOrder(id)
      .then(response => {
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  updateOrder: ({ commit }, payload) => new Promise((resolve, reject) => {
    orderRequests.updateOrder(payload.id, sanitizeOrderUpdatePayload(payload.data, {
      allowStatusUpdate: payload.allowStatusUpdate === true,
    }))
      .then(response => {
        commit('APPLY_ORDER_SAVE_GENERATED_VALUES', response.data)
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  patchOrder: ({ commit }, payload) => new Promise((resolve, reject) => {
    orderRequests.patchOrder(payload.id, payload.data)
      .then(response => {
        commit('MERGE_ORDER_EDITED', payload.data)
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  updateOrderStatus: ({ commit, dispatch }, payload) => new Promise((resolve, reject) => {
    orderRequests.changeOrderStatusAction(payload.id, payload.data)
      .then(response => {
        commit('APPLY_ORDER_WORKFLOW_SNAPSHOT', response.data.orderSnapshot)
        commit('SET_ORDER_WORKFLOW', response.data.workflow)
        resolve(response)
      })
      .catch(error => {
        if (error?.errorCode === 'order_version_conflict') {
          dispatch('fetchOrderById', payload.id)
            .finally(() => reject(error))
          return
        }

        if (isNetworkError(error)) {
          // The refresh flow owns the user-facing messaging from here on, so prevent
          // callers' parseRequestError() from also firing the contradictory network toast.
          Object.assign(error, { networkErrorHandled: true })
          refreshOrderAfterUnconfirmedWorkflowAction(dispatch, payload.id)
            .finally(() => reject(error))
          return
        }

        reject(error)
      })
  }),
  updateOrderTeam: ({ commit, dispatch, state }, payload) => new Promise((resolve, reject) => {
    const requestByAction = {
      assign: orderRequests.assignOrderTeamAction,
      transfer: orderRequests.transferOrderTeamAction,
      release: orderRequests.releaseOrderTeamAction,
    }
    const request = requestByAction[payload.action]

    if (!request) {
      reject(new Error(`Unsupported order team workflow action "${payload.action}"`))
      return
    }

    request(payload.id, payload.data)
      .then(response => {
        commit('APPLY_ORDER_WORKFLOW_SNAPSHOT', response.data.orderSnapshot)
        commit('SET_ORDER_WORKFLOW', response.data.workflow)
        resolve(response)
      })
      .catch(error => {
        if (error?.errorCode === 'order_version_conflict') {
          dispatch('fetchOrderById', payload.id)
            .then(() => {
              if (payload.action === 'assign' && isTeamActivelyAssignedToOrder(state.orderEdited, payload.data?.teamId)) {
                resolve({ recoveredFromVersionConflict: true })
                return
              }

              reject(error)
            })
            .catch(() => reject(error))
          return
        }

        if (isNetworkError(error)) {
          // The refresh flow owns the user-facing messaging from here on, so prevent
          // callers' parseRequestError() from also firing the contradictory network toast.
          Object.assign(error, { networkErrorHandled: true })
          refreshOrderAfterUnconfirmedWorkflowAction(dispatch, payload.id)
            .finally(() => reject(error))
          return
        }

        reject(error)
      })
  }),
  updateOrderPatientData: ({ dispatch }, payload) => new Promise((resolve, reject) => {
    orderRequests.updateOrderPatientData(payload.id, payload.data)
      .then(response => {
        resolve(response)
      })
      .catch(error => {
        if (error?.errorCode === 'patient_info_version_conflict' && payload.orderId) {
          dispatch('fetchOrderById', payload.orderId)
            .finally(() => reject(error))
          return
        }

        reject(error)
      })
  }),
  createOrderPatientData: (_, payload) => new Promise((resolve, reject) => {
    orderRequests.createOrderPatientData(payload.orderId, payload.data)
      .then(response => {
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  deleteOrder: ({ commit }, id) => new Promise((resolve, reject) => {
    orderRequests.deleteOrder(id)
      .then(response => {
        commit('REMOVE_ORDER_RECORD', id)
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  fetchStatisticsByStartAt: ({ commit }) => new Promise((resolve, reject) => {
    orderStatisticsRequests.byStartAt()
      .then(response => {
        commit('SET_ORDER_STATISTICS_BY_START_AT', response.data)
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  fetchFinancialResultsByOrder: (_, month) => new Promise((resolve, reject) => {
    orderStatisticsRequests.financialResultsByOrder(month)
      .then(response => {
        resolve(response.data)
      })
      .catch(error => {
        reject(error)
      })
  }),
  addComment: ({ commit }, payload) => new Promise((resolve, reject) => {
    const commentData = {
      comment: payload.comment.comment,
    }

    orderRequests.addOrderComment(payload.id, commentData)
      .then(response => {
        commit('ADD_ORDER_COMMENT', payload.comment)
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  identifyOrderByNumber: (_, number) => new Promise((resolve, reject) => {
    orderRequests.identifyOrderByNumber(number)
      .then(response => {
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  searchDisease: (_, query) => new Promise((resolve, reject) => {
    orderRequests.getDiseases(query)
      .then(response => {
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  fetchSummaryByOrderId: (_, orderId) => new Promise((resolve, reject) => {
    orderRequests.getSummaryByOrderId(orderId)
      .then(response => {
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  updateSummary: (_, payload) => new Promise((resolve, reject) => {
    orderRequests.updateSummary(payload.id, payload.data)
      .then(response => {
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  requestOrderSummaryRecalculation: (_, orderId) => new Promise((resolve, reject) => {
    orderRequests.requestOrderSummaryRecalculation(orderId)
      .then(response => {
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  requestOrderSummaryRecalculationForMonth: (_, payload) => new Promise((resolve, reject) => {
    orderRequests.requestOrderSummaryRecalculationForMonth(payload)
      .then(response => {
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  optimizeOrderRoute: (_, orderId) => new Promise((resolve, reject) => {
    orderRequests.optimizeRoute(orderId)
      .then(response => {
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  manualSortOrderRoute: (_, payload) => new Promise((resolve, reject) => {
    orderRequests.manualRouteSort(payload.orderId, payload.patientInfoIds)
      .then(response => {
        resolve(response)
      })
      .catch(error => {
        reject(error)
      })
  }),
  fetchOrderTransportHistory: ({ commit, state }, payload) => new Promise((resolve, reject) => {
    commit('SET_ORDER_TRANSPORT_HISTORY_LOADING', true)

    orderRequests.fetchTransportHistory(payload.data)
      .then(response => {
        if (state.transportHistory.requestToken !== payload.requestToken) {
          resolve(response)
          return
        }

        commit('SET_ORDER_TRANSPORT_HISTORY_RESULT', response.data)
        resolve(response)
      })
      .catch(error => {
        if (state.transportHistory.requestToken !== payload.requestToken) {
          reject(error)
          return
        }

        commit('SET_ORDER_TRANSPORT_HISTORY_ERROR', true)
        reject(error)
      })
  }),
}
